import {
  siFacebook,
  siInstagram,
  siSpotify,
  siTiktok,
  siYoutube,
} from "simple-icons";

/**
 * The logos of the platforms the footer links to.
 *
 * From simple-icons rather than drawn here: these are trademarks, and a
 * hand-drawn approximation of the Instagram camera is both wrong and
 * unrecognisable - which defeats the point of an icon row, where the logo is
 * the only label a sighted visitor gets.
 *
 * The package is a plain module of path data, so an icon costs no request at
 * all: the `d` string below is bundled into the same JavaScript as everything
 * else and rendered as an inline <svg>. Bootstrap Icons, the other obvious
 * candidate, ships its brand marks as a webfont or a sprite file, which would
 * have added a request to every page for five glyphs. Nothing is loaded from a
 * CDN, and the icons work with the same guarantee the rest of the site has:
 * the version is in the lockfile and the build fails without it.
 *
 * Every mark is a single path on a 24x24 grid, and `title` is the brand's own
 * spelling - "YouTube", "TikTok" - which is what a screen reader reads out.
 */
export interface SocialIcon {
  /** The brand name, as the platform writes it. */
  label: string;
  /** The whole logo, as one path on a 24x24 grid. */
  path: string;
}

/**
 * Keyed by string rather than by SocialPlatform, and looked up rather than
 * matched.
 *
 * The platform comes off the wire, so it can be anything - and one day it will
 * be: the CMS gains a platform, an editor fills it in, and every deployment of
 * this site older than that change receives a name it has never heard of. A
 * lookup that misses is an icon this version cannot draw and nothing more, so
 * the entry is skipped and the rest of the row renders. A union comparison
 * would have made that a type error here and an empty footer there.
 */
export const SOCIAL_ICONS: Record<string, SocialIcon> = {
  instagram: { label: siInstagram.title, path: siInstagram.path },
  facebook: { label: siFacebook.title, path: siFacebook.path },
  youtube: { label: siYoutube.title, path: siYoutube.path },
  spotify: { label: siSpotify.title, path: siSpotify.path },
  tiktok: { label: siTiktok.title, path: siTiktok.path },
};
